import { useState } from 'react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Upload, FileText, Eye, Download } from 'lucide-react'
import { toast } from 'sonner'
import { 
  parseProjectMarkdown, 
  parseBlogMarkdown, 
  convertToMultilingualProject,
  convertToMultilingualBlog,
  generateProjectMarkdownTemplate,
  generateBlogMarkdownTemplate
} from '@/lib/markdownParser'
import { useCreateMultilingualProject } from '@/hooks/useMultilingualProjects'
import { useCreateMultilingualBlog } from '@/hooks/useMultilingualBlogs'

interface MarkdownImporterProps {
  type: 'project' | 'blog'
}

export function MarkdownImporter({ type }: MarkdownImporterProps) {
  const { t } = useTranslation()
  const [markdown, setMarkdown] = useState('')
  const [parsedData, setParsedData] = useState<any>(null)
  const [isImporting, setIsImporting] = useState(false)

  const createProject = useCreateMultilingualProject()
  const createBlog = useCreateMultilingualBlog()

  const handleParse = () => {
    if (!markdown.trim()) {
      toast.error(t('admin.import.errors.emptyMarkdown'))
      return
    }

    try {
      if (type === 'project') {
        const { frontmatter, content } = parseProjectMarkdown(markdown)
        const projectData = convertToMultilingualProject(frontmatter, content)
        setParsedData({ frontmatter, content, projectData })
      } else {
        const { frontmatter, content } = parseBlogMarkdown(markdown)
        const blogData = convertToMultilingualBlog(frontmatter, content)
        setParsedData({ frontmatter, content, blogData })
      }
      toast.success(t('admin.import.parseSuccess'))
    } catch (error) {
      console.error('Parse error:', error)
      toast.error(t('admin.import.errors.parseError'))
    }
  }

  const handleImport = async () => {
    if (!parsedData) return

    setIsImporting(true)
    try {
      if (type === 'project') {
        await createProject.mutateAsync(parsedData.projectData)
        toast.success(t('admin.import.importSuccess.project'))
      } else {
        await createBlog.mutateAsync(parsedData.blogData)
        toast.success(t('admin.import.importSuccess.blog'))
      }
      
      // 清空表单
      setMarkdown('')
      setParsedData(null)
    } catch (error) {
      console.error('Import error:', error)
      toast.error(t('admin.import.errors.importError'))
    } finally {
      setIsImporting(false)
    }
  }

  const downloadTemplate = () => {
    const template = type === 'project' 
      ? generateProjectMarkdownTemplate()
      : generateBlogMarkdownTemplate()
    
    const blob = new Blob([template], { type: 'text/markdown' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${type}-template.md`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">
            {t(`admin.import.${type}.title`)}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t(`admin.import.${type}.description`)}
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          {t('admin.import.downloadTemplate')}
        </Button>
      </div>

      <Tabs defaultValue="input" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="input" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            {t('admin.import.tabs.input')}
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-2" disabled={!parsedData}>
            <Eye className="h-4 w-4" />
            {t('admin.import.tabs.preview')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="input" className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('admin.import.markdownContent')}
            </label>
            <Textarea
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder={t(`admin.import.${type}.placeholder`)}
              className="min-h-[300px] font-mono text-sm"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={handleParse} disabled={!markdown.trim()}>
              <FileText className="h-4 w-4 mr-2" />
              {t('admin.import.parse')}
            </Button>
            {parsedData && (
              <Button 
                onClick={handleImport} 
                disabled={isImporting}
                className="bg-green-600 hover:bg-green-700"
              >
                <Upload className="h-4 w-4 mr-2" />
                {isImporting ? t('admin.import.importing') : t('admin.import.import')}
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          {parsedData && (
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    {parsedData.frontmatter.translations?.zh?.title || 'Untitled'}
                    <div className="flex gap-2">
                      <Badge>{parsedData.frontmatter.status || 'planned'}</Badge>
                      {parsedData.frontmatter.is_featured && (
                        <Badge variant="secondary">Featured</Badge>
                      )}
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {parsedData.frontmatter.translations?.zh?.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {parsedData.frontmatter.translations?.zh?.description}
                  </p>
                  
                  {parsedData.frontmatter.tags && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2">Tags:</h4>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(parsedData.frontmatter.tags).map(([lang, tags]) => (
                          <div key={lang} className="flex flex-wrap gap-1">
                            <Badge variant="outline" className="text-xs">{lang.toUpperCase()}</Badge>
                            {(tags as string[]).map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {type === 'project' && parsedData.frontmatter.tech_stack && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium mb-2">Tech Stack:</h4>
                      <div className="flex flex-wrap gap-1">
                        {parsedData.frontmatter.tech_stack.map((tech: string, index: number) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {type === 'blog' && (
                    <div className="text-sm text-muted-foreground">
                      <p>Slug: /{parsedData.frontmatter.slug}</p>
                      {parsedData.frontmatter.author && (
                        <p>Author: {parsedData.frontmatter.author}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">中文内容</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-3 rounded">
                        {parsedData.content.zh || '无中文内容'}
                      </pre>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">English Content</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-3 rounded">
                        {parsedData.content.en || 'No English content'}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}